import React, { useRef, useState } from 'react';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

import icons from '../../assets/icons';
import CopyButton from '../copyButton';

const formatAPIKeyDate = value => new Date(value).toLocaleDateString(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric'
});

const modalCloseButton = onClose => (
  <button type="button" className="close" aria-label="Close" onClick={onClose}>
    <span aria-hidden="true">&times;</span>
  </button>
);

const APIKeys = ({ apiKeys = [], hasMore, isLoading, onCreate, onLoadMore, onRevoke }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [expireTime, setExpireTime] = useState('');
  const [secret, setSecret] = useState('');
  const [error, setError] = useState('');
  const [expireError, setExpireError] = useState('');
  const [copyStatus, setCopyStatus] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [keyToRevoke, setKeyToRevoke] = useState(null);
  const [isRevoking, setIsRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState('');
  const expireInput = useRef(null);

  const closeModal = () => {
    setIsModalOpen(false);
    setDisplayName('');
    setExpireTime('');
    setSecret('');
    setError('');
    setExpireError('');
    setCopyStatus('');
  };

  const create = async () => {
    const name = displayName.trim();
    if (!name) {
      setError('Name is required.');
      return;
    }

    const expiry = expireTime ? new Date(expireTime) : null;
    if (expireInput.current?.validity.badInput || (expiry && Number.isNaN(expiry.getTime()))) {
      setExpireError('Enter a valid expiry date.');
      return;
    }
    if (expiry && expiry <= new Date()) {
      setExpireError('Expiry date must be in the future.');
      return;
    }

    setError('');
    setExpireError('');
    setIsCreating(true);
    try {
      const response = await onCreate({
        displayName: name,
        expireTime: expiry ? expiry.toISOString() : undefined
      });
      setSecret(response.secret);
      setCopyStatus('');
    } catch (error) {
      if (error.errors?.some(issue => issue.field === 'api_key.expire_time')) {
        setExpireError('Choose a valid future expiry date.');
      } else {
        setError(error.message || error.detail || 'Unable to create API key.');
      }
    } finally {
      setIsCreating(false);
    }
  };

  const revoke = async () => {
    setIsRevoking(true);
    try {
      await onRevoke(keyToRevoke.id);
      setKeyToRevoke(null);
    } catch (error) {
      setRevokeError(error.message || 'Unable to revoke API key.');
    } finally {
      setIsRevoking(false);
    }
  };

  return (
    <div className="profile-cards api-keys">
      <div className="card">
        <div className="card-header">
          <img src={icons.key} alt="" />
          <h3>API Keys</h3>
          <button className="btn btn-primary btn-sm" onClick={() => setIsModalOpen(true)}>
            Create API Key
          </button>
        </div>
        <div className="card-body">
          <p className="api-keys-description">Create API keys for programs that use the AdMobilize public API.</p>
        {isLoading ? (
          <p className="mb-0">Loading API keys...</p>
        ) : apiKeys.length === 0 ? (
          <p className="mb-0">No API keys yet.</p>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Prefix</th>
                  <th>Created</th>
                  <th>Expires</th>
                  <th>Status</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {apiKeys.map(apiKey => (
                  <tr key={apiKey.id}>
                    <td>{apiKey.displayName}</td>
                    <td><code className="api-key-prefix" title={apiKey.keyPrefix}>{apiKey.keyPrefix}</code></td>
                    <td>{apiKey.createTime ? <time dateTime={apiKey.createTime} title={new Date(apiKey.createTime).toLocaleString()}>{formatAPIKeyDate(apiKey.createTime)}</time> : '-'}</td>
                    <td>{apiKey.expireTime ? <time dateTime={apiKey.expireTime} title={new Date(apiKey.expireTime).toLocaleString()}>{formatAPIKeyDate(apiKey.expireTime)}</time> : 'Never'}</td>
                    <td>{apiKey.status === 'API_KEY_STATUS_EXPIRED' ? 'Expired' : 'Active'}</td>
                    <td className="text-right">
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => {
                          setRevokeError('');
                          setKeyToRevoke(apiKey);
                        }}
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {hasMore && !isLoading && (
          <button className="btn btn-secondary btn-sm mt-3" onClick={onLoadMore}>
            Load more
          </button>
        )}
        </div>
      </div>

      <Modal isOpen={isModalOpen} toggle={closeModal} fade={false} centered>
        <ModalHeader close={modalCloseButton(closeModal)}>{secret ? 'Save API Key' : 'Create API Key'}</ModalHeader>
        {!secret ? (
          <>
            <ModalBody>
              <div className="form-group">
                <label htmlFor="api-key-name">Name</label>
                <input
                  id="api-key-name"
                  className="form-control"
                  maxLength="128"
                  onChange={event => setDisplayName(event.target.value)}
                  value={displayName}
                />
              </div>
              <div className="form-group">
                <label htmlFor="api-key-expire-time">Expires at (optional)</label>
                <input
                  id="api-key-expire-time"
                  ref={expireInput}
                  className={expireError ? 'form-control is-invalid' : 'form-control'}
                  aria-invalid={Boolean(expireError)}
                  aria-describedby={expireError ? 'api-key-expire-error' : undefined}
                  onChange={event => {
                    setExpireTime(event.target.value);
                    setExpireError('');
                  }}
                  type="datetime-local"
                  value={expireTime}
                />
                {expireError && <div id="api-key-expire-error" className="invalid-feedback d-block" role="alert">{expireError}</div>}
              </div>
              {error && <div className="text-danger">{error}</div>}
            </ModalBody>
            <ModalFooter>
              <button className="btn btn-secondary" disabled={isCreating} onClick={closeModal}>
                Cancel
              </button>
              <button className="btn btn-primary" disabled={isCreating} onClick={create}>
                Generate API Key
              </button>
            </ModalFooter>
          </>
        ) : (
          <>
            <ModalBody>
              <p>Copy this key now. It will not be shown again.</p>
              <div className="api-key-secret">
                <code id="api-key-secret">{secret}</code>
                <CopyButton id="api-key-secret" tooltip="Copy API key" onCopyResult={setCopyStatus} text={secret} />
              </div>
              {copyStatus && (
                <p className={`api-key-copy-status mt-2 mb-0 ${copyStatus === 'success' ? 'text-success' : 'text-danger'}`} role="status">
                  {copyStatus === 'success' ? 'Copied to clipboard' : 'Copy failed. Select the key and copy it manually.'}
                </p>
              )}
            </ModalBody>
            <ModalFooter>
              <button className="btn btn-primary" onClick={closeModal}>
                I&apos;ve Saved It
              </button>
            </ModalFooter>
          </>
        )}
      </Modal>

      <Modal isOpen={Boolean(keyToRevoke)} toggle={() => setKeyToRevoke(null)} fade={false} centered>
        <ModalHeader close={modalCloseButton(() => setKeyToRevoke(null))}>Revoke API Key</ModalHeader>
        <ModalBody>
          Revoke {keyToRevoke?.displayName}? Programs using this key will stop working immediately.
          {revokeError && <div className="text-danger mt-3">{revokeError}</div>}
        </ModalBody>
        <ModalFooter>
          <button className="btn btn-secondary" disabled={isRevoking} onClick={() => setKeyToRevoke(null)}>
            Cancel
          </button>
          <button className="btn btn-danger" disabled={isRevoking} onClick={revoke}>
            Revoke Key
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default APIKeys;
